import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { MapPin, Check } from 'lucide-react';
import type { SelfServiceFormData } from '../../../types/selfService';
import type { PublisherMarket } from '../../../types/selfService';
import { FieldCard, FieldError, SectionHeading, tintFromAccent } from '../steps/_shared';

interface MarketSelectorProps {
  markets: PublisherMarket[];
  accentColor?: string;
}

const MarketSelector: React.FC<MarketSelectorProps> = ({ markets, accentColor = '#3b82f6' }) => {
  const { control, formState: { errors } } = useFormContext<SelfServiceFormData>();

  return (
    <div>
      <SectionHeading>Target markets</SectionHeading>
      <Controller
        name="targetMarkets"
        control={control}
        render={({ field }) => {
          const selected = field.value ?? [];
          const toggle = (dmaCode: string) => {
            const updated = selected.includes(dmaCode)
              ? selected.filter((c: string) => c !== dmaCode)
              : [...selected, dmaCode];
            field.onChange(updated);
          };
          return (
            <FieldCard accentColor={accentColor}>
              {markets.map((market) => {
                const isSelected = selected.includes(market.dmaCode);
                return (
                  <button
                    key={market.dmaCode}
                    type="button"
                    onClick={() => toggle(market.dmaCode)}
                    className="w-full flex items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-slate-50"
                    style={{
                      backgroundColor: isSelected ? tintFromAccent(accentColor, 0.06) : undefined,
                    }}
                  >
                    <div
                      className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors"
                      style={{
                        borderColor: isSelected ? accentColor : 'rgb(203, 213, 225)',
                        backgroundColor: isSelected ? accentColor : 'transparent',
                      }}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <MapPin
                          className="w-3.5 h-3.5 shrink-0"
                          style={{ color: isSelected ? accentColor : 'rgb(100,116,139)' }}
                        />
                        <span className="text-[14px] font-semibold tracking-tight text-slate-900 truncate">
                          {market.dmaName}
                        </span>
                      </div>
                      <div className="text-[12px] text-slate-500 mt-0.5 ml-5 truncate">
                        {market.channels.join(', ')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </FieldCard>
          );
        }}
      />
      <FieldError message={errors.targetMarkets?.message} />

    </div>
  );
};

export default MarketSelector;
